/* =========================================================================
   Fiena — single entry point for all section behaviours.
   Behaviours:
     - Hero gallery crossfade (5-frame, configurable interval).
     - FAQ accordion (one open at a time).
     - Parallax background translate (split-pair, landscape).
     - Topbar scroll observer (hide over hero, light scheme over cream).
     - Nav header (right-hand panel) and journal article enhancements.
   Theme-editor aware: cleans up on shopify:section:unload.
   ========================================================================= */
(function () {
  'use strict';

  // ---- Hero gallery -------------------------------------------------------

  function initGallery(root) {
    var frames = root.querySelectorAll('.fn-hero__frame');
    if (frames.length < 2) return;
    var interval = parseInt(root.getAttribute('data-interval'), 10) || 5000;
    var idx = 0;
    var timer = setInterval(function () {
      frames[idx].classList.remove('is-active');
      idx = (idx + 1) % frames.length;
      frames[idx].classList.add('is-active');
    }, interval);
    root.__fnGalleryTimer = timer;
  }

  function destroyGallery(root) {
    if (root && root.__fnGalleryTimer) {
      clearInterval(root.__fnGalleryTimer);
      root.__fnGalleryTimer = null;
    }
  }

  // ---- FAQ accordion ------------------------------------------------------

  function initFaq(root) {
    var items = root.querySelectorAll('.fn-faq__item');
    if (!items.length) return;

    items.forEach(function (item) {
      var q = item.querySelector('.fn-faq__q');
      if (!q || q.__fnBound) return;
      q.__fnBound = true;
      q.addEventListener('click', function () {
        var wasOpen = item.classList.contains('is-open');
        items.forEach(function (i) {
          i.classList.remove('is-open');
          var b = i.querySelector('.fn-faq__q');
          if (b) b.setAttribute('aria-expanded', 'false');
        });
        if (!wasOpen) {
          item.classList.add('is-open');
          q.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  // ---- Parallax -----------------------------------------------------------
  // Single page-wide observer; updates every .fn-parallax via rAF on scroll.

  var parallaxItems = [];
  var parallaxTicking = false;
  var parallaxBound = false;

  function refreshParallax() {
    parallaxItems = Array.prototype.slice.call(document.querySelectorAll('.fn-parallax'));
  }

  function updateParallax() {
    var vh = window.innerHeight;
    parallaxItems.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > vh + 200) return;
      var center = rect.top + rect.height / 2;
      var off = (center - vh / 2) / vh;
      var amount = parseInt(el.getAttribute('data-parallax'), 10);
      if (isNaN(amount)) amount = 60;
      el.style.transform = 'translate3d(0,' + (-off * amount) + 'px,0)';
    });
  }

  function onParallaxScroll() {
    if (parallaxTicking) return;
    parallaxTicking = true;
    requestAnimationFrame(function () { updateParallax(); parallaxTicking = false; });
  }

  function bindParallax() {
    if (parallaxBound) return;
    parallaxBound = true;
    window.addEventListener('scroll', onParallaxScroll, { passive: true });
    window.addEventListener('resize', updateParallax);
  }

  // ---- Topbar scroll state ------------------------------------------------
  // Single page-wide observer. Hides while inside the hero; switches to
  // light colour scheme when scrolled over a cream/light section.

  var topbarBound = false;
  var topbarState = { hidden: false, light: false };

  function checkTopbar() {
    var topbar = document.querySelector('.fn-topbar');
    if (!topbar) return;

    var hero = document.querySelector('[data-fn-hero]');
    var lightZones = document.querySelectorAll('[data-fn-light-zone]');

    var shouldHide = false;
    if (hero) {
      var heroBottom = hero.getBoundingClientRect().bottom;
      shouldHide = heroBottom > window.innerHeight * 0.4;
    }
    if (shouldHide !== topbarState.hidden) {
      topbar.classList.toggle('is-hidden', shouldHide);
      topbarState.hidden = shouldHide;
    }

    var shouldLight = false;
    lightZones.forEach(function (zone) {
      var r = zone.getBoundingClientRect();
      if (r.top < 60 && r.bottom > 60) shouldLight = true;
    });
    if (shouldLight !== topbarState.light) {
      topbar.classList.toggle('is-light', shouldLight);
      topbarState.light = shouldLight;
    }
  }

  function bindTopbar() {
    if (topbarBound) return;
    topbarBound = true;
    window.addEventListener('scroll', checkTopbar, { passive: true });
    window.addEventListener('resize', checkTopbar);
  }

  // ---- Nav header ---------------------------------------------------------
  // Desktop: inline links in the bar. Tablet/phone: the Menu button opens a
  // panel from the right. Body-scroll lock, ESC + backdrop close, focus trap,
  // focus returns to the toggle. Bar gets a shadow once the page scrolls.

  function initNavHeader(root) {
    var toggle  = root.querySelector('[data-fn-nav-toggle]');
    var overlay = root.querySelector('[data-fn-nav-overlay]');
    var closer  = root.querySelector('[data-fn-nav-close]');
    var backdrop = root.querySelector('[data-fn-nav-backdrop]');
    if (!toggle || !overlay) return;

    var desktop = window.matchMedia('(min-width: 1200px)');

    function focusables() {
      return Array.prototype.slice.call(
        overlay.querySelectorAll('a[href], button:not([disabled])')
      ).filter(function (el) { return el.offsetParent !== null; });
    }
    function open() {
      root.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      setTimeout(function () { if (closer) closer.focus(); }, 60);
    }
    function close(returnFocus) {
      if (!root.classList.contains('is-open')) return;
      root.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (returnFocus !== false) toggle.focus();
    }
    function onKey(e) {
      if (!root.classList.contains('is-open')) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      var items = focusables();
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    function onScroll() { root.classList.toggle('is-scrolled', window.scrollY > 8); }
    function onBreakpoint(e) { if (e.matches) close(false); }
    function onPanelClick(e) {
      var link = e.target.closest && e.target.closest('a[href]');
      if (link) close(false);
    }

    toggle.addEventListener('click', function () {
      if (root.classList.contains('is-open')) { close(); } else { open(); }
    });
    if (closer)   closer.addEventListener('click', function () { close(); });
    if (backdrop) backdrop.addEventListener('click', function () { close(); });
    overlay.addEventListener('click', onPanelClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, { passive: true });
    if (desktop.addEventListener) desktop.addEventListener('change', onBreakpoint);
    onScroll();

    root.__fnNavCleanup = function () {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll);
      if (desktop.removeEventListener) desktop.removeEventListener('change', onBreakpoint);
      document.body.style.overflow = '';
    };
  }

  function destroyNavHeader(root) {
    if (root && root.__fnNavCleanup) {
      root.__fnNavCleanup();
      root.__fnNavCleanup = null;
    }
  }

  // ---- Journal article ----------------------------------------------------
  // Reading-progress bar, figure captions from image alt text, and the
  // contents list highlighting whichever section is on screen.

  function initJournalArticle(root) {
    var prose = root.querySelectorAll('.fj-prose');
    prose.forEach(function (box) {
      box.querySelectorAll('img').forEach(function (img) {
        if (img.closest('figure')) return;
        var fig = document.createElement('figure');
        fig.className = 'fj-figure';
        img.parentNode.insertBefore(fig, img);
        fig.appendChild(img);
        var alt = (img.getAttribute('alt') || '').trim();
        if (alt) {
          var cap = document.createElement('figcaption');
          cap.textContent = alt;
          fig.appendChild(cap);
        }
      });
    });

    var bar = root.querySelector('[data-fj-progress]');
    var body = root.querySelector('[data-fj-body]');
    function onScroll() {
      if (!bar || !body) return;
      var rect = body.getBoundingClientRect();
      var total = rect.height - window.innerHeight * 0.6;
      var done = Math.min(Math.max(-rect.top + window.innerHeight * 0.2, 0), total);
      bar.style.transform = 'scaleX(' + (total > 0 ? done / total : 0) + ')';
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();

    var links = root.querySelectorAll('[data-fj-toc] a[href^="#"]');
    var observer = null;
    if (links.length && 'IntersectionObserver' in window) {
      var map = {};
      links.forEach(function (a) { map[a.getAttribute('href').slice(1)] = map[a.getAttribute('href').slice(1)] || []; map[a.getAttribute('href').slice(1)].push(a); });
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          links.forEach(function (a) { a.classList.remove('is-active'); });
          (map[en.target.id] || []).forEach(function (a) { a.classList.add('is-active'); });
        });
      }, { rootMargin: '-20% 0px -70% 0px' });
      Object.keys(map).forEach(function (id) {
        var el = document.getElementById(id);
        if (el) observer.observe(el);
      });
    }

    root.__fjCleanup = function () {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (observer) observer.disconnect();
    };
  }

  function destroyJournalArticle(root) {
    if (root && root.__fjCleanup) { root.__fjCleanup(); root.__fjCleanup = null; }
  }

  // ---- Section init / destroy --------------------------------------------

  function initSection(root) {
    if (!root || root.__fnInit) return;
    root.__fnInit = true;

    if (root.matches('[data-fn-hero]'))         initGallery(root);
    if (root.matches('[data-fn-nav-header]'))   initNavHeader(root);
    if (root.matches('[data-fj-article]'))      initJournalArticle(root);
    if (root.querySelector('.fn-faq'))          initFaq(root);
    if (root.querySelector('.fn-parallax'))     { refreshParallax(); bindParallax(); updateParallax(); }
    if (root.matches('.fn-topbar') || root.querySelector('.fn-topbar')) {
      bindTopbar();
      checkTopbar();
    }
  }

  function destroySection(root) {
    if (!root) return;
    destroyGallery(root);
    destroyNavHeader(root);
    destroyJournalArticle(root);
    root.__fnInit = false;
    refreshParallax();
  }

  function initAll() {
    document.querySelectorAll('[data-fn-section]').forEach(initSection);
    // Topbar lives in header-group; not a fn section by itself but still needs observer.
    if (document.querySelector('.fn-topbar')) { bindTopbar(); checkTopbar(); }
    refreshParallax();
    if (parallaxItems.length) { bindParallax(); updateParallax(); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor lifecycle ---------------------------------------------------

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-fn-section]') || e.target.querySelector('.fn-topbar');
    if (root) initSection(root);
    refreshParallax();
    updateParallax();
    checkTopbar();
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-fn-section]');
    if (root) destroySection(root);
    refreshParallax();
  });

  document.addEventListener('shopify:section:select', function (e) {
    var root = e.target.querySelector('[data-fn-section]');
    if (root) { destroySection(root); initSection(root); }
  });
})();
